import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import { motion } from '../motion';

const BOOK_URL = 'https://calendar.app.google/6g62S4xtRa8eWipt5';

const LINKS = [
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/monelillm', label: 'MoneliLLM' },
  { to: '/case-studies', label: 'Case Studies' },
  { to: '/blog', label: 'Blog' },
  { to: '/demos', label: 'Demos' },
  { to: '/contact', label: 'Contact' },
];

/*
 * The nav is transparent (light text) while it sits over an element marked
 * [data-nav-over] (the home hero, PageHero dark/image), solid otherwise.
 * It hides on scroll down and returns on scroll up.
 */
function Navbar() {
  const { pathname } = useLocation();
  const [over, setOver] = useState(false);
  const [solid, setSolid] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const openRef = useRef(false);
  openRef.current = open;

  // Detect a dark hero on the new route before paint.
  useLayoutEffect(() => {
    setOver(!!document.querySelector('[data-nav-over]'));
  }, [pathname]);

  // Solid / hidden on scroll.
  useEffect(() => {
    let lastY = window.pageYOffset;
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.pageYOffset;
      const heroEl = document.querySelector('[data-nav-over]');
      const solidAt = heroEl ? Math.max(40, heroEl.offsetTop + heroEl.offsetHeight - 90) : 10;
      setSolid(y > solidAt);
      const dy = y - lastY;
      if (openRef.current) setHidden(false);
      else if (y > 420 && dy > 4) setHidden(true);
      else if (dy < -4 || y < 160) setHidden(false);
      lastY = y;
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname]);

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus && toggleRef.current) toggleRef.current.focus({ preventScroll: true });
  }, []);

  // Close the menu on navigation.
  useEffect(() => { setOpen(false); }, [pathname]);

  // Menu side effects: scroll lock, inert background, focus trap, Esc.
  useEffect(() => {
    const html = document.documentElement;
    const bg = Array.from(document.querySelectorAll('#main, .site-footer, .chat-float-stack'));
    if (!open) {
      html.classList.remove('menu-open');
      bg.forEach((el) => el.removeAttribute('inert'));
      motion.start();
      return undefined;
    }
    html.classList.add('menu-open');
    bg.forEach((el) => el.setAttribute('inert', ''));
    motion.stop();
    const focusTimer = setTimeout(() => {
      const first = menuRef.current && menuRef.current.querySelector('a');
      if (first) first.focus({ preventScroll: true });
    }, 350);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab' || !menuRef.current) return;
      const f = [toggleRef.current, ...menuRef.current.querySelectorAll('a[href], button:not([disabled])')];
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    const onResize = () => { if (window.innerWidth >= 1200) setOpen(false); };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      html.classList.remove('menu-open');
      bg.forEach((el) => el.removeAttribute('inert'));
      motion.start();
    };
  }, [open, close]);

  const navClass = [
    'site-nav',
    over ? 'is-over' : '',
    solid ? 'is-solid' : '',
    hidden && !open ? 'is-hidden' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <nav className={navClass} aria-label="Primary">
        <div className="container site-nav__inner">
          <Link className="brand" to="/" aria-label="Moneli Automation — home">
            <span className="brand__mark"><img src={logo} alt="" width="40" height="40" /></span>
            <span className="brand__text">
              <span className="brand__name">Moneli</span>
              <span className="brand__sub">Automation</span>
            </span>
          </Link>

          <ul className="nav-links">
            {LINKS.map((item) => (
              <li key={item.to}>
                <NavLink className="nav-link-m" to={item.to}>{item.label}</NavLink>
              </li>
            ))}
          </ul>

          <div className="nav-actions">
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm nav-cta"
              data-magnetic="0.2"
            >
              Book a Demo
            </a>
            <button
              ref={toggleRef}
              type="button"
              className="menu-toggle"
              aria-controls="site-menu"
              aria-expanded={open}
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((v) => !v)}
            >
              <span className="menu-toggle__bars" aria-hidden="true"><span /><span /></span>
            </button>
          </div>
        </div>
      </nav>

      <div
        id="site-menu"
        ref={menuRef}
        className={`menu-overlay${open ? ' is-open' : ''}`}
        aria-hidden={!open}
        {...(!open ? { inert: '' } : {})}
      >
        <ul className="menu-overlay__list">
          {[{ to: '/', label: 'Home' }, ...LINKS].map((item, i) => (
            <li key={item.to}>
              <NavLink
                className="menu-overlay__link"
                to={item.to}
                end={item.to === '/'}
                style={{ '--i': i }}
                onClick={() => close(false)}
              >
                <small>{String(i + 1).padStart(2, '0')}</small>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="menu-overlay__foot">
          <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
            <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
            Book a Demo
          </a>
          <div className="d-flex gap-3">
            <a href="https://www.linkedin.com/company/moneliautomation/?viewAsMember=true" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a href="https://www.youtube.com/@moneliautomation" target="_blank" rel="noopener noreferrer">YouTube</a>
          </div>
        </div>
      </div>
    </>
  );
}

export default Navbar;
